import { Injectable } from '@angular/core';
import { ApiService } from '../../../../core/services/api.service';
import { Observable } from 'rxjs';
import {
    IPayroll,
    IPayrollEntry,
    ITotals,
    IPayslip,
    IChartEntry,
    IInvoice,
    ISummaryInvoice,
    ITimeRecordResponse,
    ILocationTimeTracking,
    IPayrollFilters,
    IPayDetails,
    IExpense,
    IPaymentsSummary,
    IPaymentSubcontractor,
    IPaymentAuth,
    ITimesheetMoreInfoElement,
} from './types';
import { IPagination } from '../../../../modules/authorization/store/types';
import { map } from 'rxjs/operators';
import { ITimeRecord } from '../../time-records/store/types';

@Injectable({
    providedIn: 'root',
})
export class PayrollService {
    constructor(private apiService: ApiService) {}

    public fetchAll(data: IPayrollFilters): Observable<IPagination<IPayroll>> {
        if (!data.companyID) {
            delete data.companyID;
        }
        return this.apiService.get(data.subcontractor ? '/subcontractors/payrolls' : '/payrolls', data);
    }

    public fetchOne(id: string): Observable<IPayroll> {
        return this.apiService.get(`/payrolls/${id}`);
    }

    public add(id: string, data: any): Observable<IPayroll> {
        return this.apiService.post(`/companies/${id}/payrolls`, data);
    }

    public update(id: string, data: any): Observable<any> {
        return this.apiService.put(`/payrolls/${id}`, data);
    }

    public delete(id: string): Observable<any> {
        return this.apiService.delete(`/payrolls/${id}`);
    }

    public bulkActionPayroll(action: string, ids: string[], active?: boolean): Observable<any> {
        return this.apiService.post(`/payrolls/bulk/${action}`, { ids, active });
    }

    public bulkDownloadPayroll(action: string, ids: string[]): Observable<any> {
        return this.apiService.downloadPost(`/payrolls/bulk/${action}`, { ids });
    }

    public runPayroll(id: string): Observable<any> {
        return this.apiService.put(`/payrolls/${id}/run`);
    }

    public cancelPayroll(id: string, preserveLocations: boolean = false): Observable<any> {
        return this.apiService.put(`/payrolls/${id}/cancel`, { preserve_locations: preserveLocations });
    }

    public checkPayrollLocations(id: string): Observable<{ locations_match: boolean }> {
        return this.apiService.get(`/payrolls/${id}/locations-check`);
    }

    public sendNotifications(id: string): Observable<any> {
        return this.apiService.put(`/payrolls/${id}/notifications`);
    }

    public fetchPayrollEntriesList(id: string, data: any): Observable<IPagination> {
        return this.apiService.get(`/payrolls/${id}/payroll-entries`, data);
    }

    public fetchPayrollEntriesSum(id: string, worked: number): Observable<ITotals> {
        return this.apiService.get(`/payrolls/${id}/payroll-entries/aggregates`, { worked });
    }

    public addPayrollEntry(id: string, data: any): Observable<IPayrollEntry> {
        return this.apiService.post(`/payrolls/${id}/payroll-entries`, data);
    }

    public updatePayrollEntry(id: string, data: any): Observable<any> {
        return this.apiService.put(`/payroll-entries/${id}`, data);
    }

    public updateLocationStat(payrollEntryID: string, locationStatID: string, data: any): Observable<any> {
        return this.apiService.put(`/payroll-entries/${payrollEntryID}/locations-stats/${locationStatID}`, data);
    }

    public updateAdjustmentStat(payrollEntryID: string, adjustmentStatID: string, data: any): Observable<any> {
        return this.apiService.put(`/payroll-entries/${payrollEntryID}/adjustments-stats/${adjustmentStatID}`, data);
    }

    public deletePayrollEntry(id: string): Observable<any> {
        return this.apiService.delete(`/payroll-entries/${id}`);
    }

    public fetchAllSubcontractorsReport(id: string, data: any): Observable<IPagination<IPayDetails>> {
        return this.apiService.get(`/payrolls/${id}/subcontractors/report`, data);
    }

    public fetchAllPayslips(id: string, data: any): Observable<IPagination<IPayDetails>> {
        return this.apiService.get(`/payrolls/${id}/payslips`, data);
    }

    public fetchOnePayslip(id: string): Observable<IPayslip> {
        return this.apiService.get(`/payslips/${id}`);
    }

    public exportPayslipsBacs(id: string, date: Date): Observable<any> {
        return this.apiService.downloadPost(`/payrolls/${id}/payslips/bacs`, { date });
    }

    public exportPayslipsRevolut(id: string, type: 'UK' | 'International'): Observable<any> {
        return this.apiService.downloadGet(`/payrolls/${id}/payslips/csv`, { type });
    }

    public exportPayslipsSubaccountsRevolut(id: string): Observable<any> {
        return this.apiService.downloadGet(`/payrolls/${id}/payslips/subaccounts`);
    }

    public exportPayslips(id: string): Observable<any> {
        return this.apiService.downloadGet(`/payrolls/${id}/payslips/export`);
    }

    public downloadPayslips(id: string): Observable<any> {
        return this.apiService.downloadGet(`/payrolls/${id}/payslips/pdf`);
    }

    public downloadPayslip(id: string): Observable<any> {
        return this.apiService.downloadGet(`/payslips/${id}/pdf`);
    }

    public bulkDownloadPayslips(action: string, ids: string[], data?: any): Observable<any> {
        return this.apiService.downloadPost(`/payslips/bulk/${action}`, { ids, ...data });
    }

    public bulkResendPayslips(action: string, ids: string[], data?: any): Observable<any> {
        return this.apiService.post(`/payslips/bulk/${action}`, { ids, ...data });
    }

    public fetchChart(companyID: string): Observable<IChartEntry[]> {
        return this.apiService.get(`/companies/${companyID}/payrolls/chart`).pipe(map((res) => res.results));
    }

    public fetchAllInvoics(id: string, data: any): Observable<IPagination<IInvoice>> {
        return this.apiService.get(`/payrolls/${id}/invoices`, data);
    }

    public fetchOneInvoice(id: string): Observable<IInvoice> {
        return this.apiService.get(`/invoices/${id}`);
    }

    public fetchSummaryInvoice(id: string): Observable<ISummaryInvoice> {
        return this.apiService.get(`/summary-invoices/${id}`);
    }

    public exportInvoices(id: string): Observable<any> {
        return this.apiService.downloadGet(`/payrolls/${id}/invoices/export`);
    }

    public downloadSummaryInvoice(id: string): Observable<any> {
        return this.apiService.downloadGet(`/summary-invoices/${id}/pdf`);
    }

    public downloadInvoice(id: string): Observable<any> {
        return this.apiService.downloadGet(`/invoices/${id}/pdf`);
    }

    public resendInvoice(id: string): Observable<any> {
        return this.apiService.get(`/invoices/${id}/resend`);
    }

    public resendAllInvoices(id: string): Observable<any> {
        return this.apiService.post(`/payrolls/${id}/invoices/resend`);
    }

    public bulkDownloadInvoices(action: string, ids: string[], data?: any): Observable<any> {
        return this.apiService.downloadPost(`/invoices/bulk/${action}`, { ids, ...data });
    }

    public bulkActionEntries(payrollID: string, action: string, ids: string[], active?: boolean): Observable<any> {
        return this.apiService.post(`/payrolls/${payrollID}/payroll-entries/bulk/${action}`, { ids, active });
    }

    public exportPayroll(id: string): Observable<any> {
        return this.apiService.downloadGet(`/payrolls/${id}/payroll-entries/export?worked=1&sort=subcontractor_nameASC&page=1&results=9999`);
    }

    public fetchAllSubcontractorTimeRecords(id: string, data: { start_date: Date; end_date: Date; timesheet_id: string }): Observable<ILocationTimeTracking[]> {
        return this.apiService.get(`/time_records/getSubcontractorTimeRecords/one/${id}`, data);
    }

    public fetchSubcontractorAcceptedTimeRecords(
        id: string,
        data: { start_date: Date; end_date: Date; timesheet_id: string },
    ): Observable<ILocationTimeTracking[]> {
        return this.apiService.get(`/time_records/getSubcontractorTimeRecords/assigned/${id}`, data);
    }

    public acceptTimeRecord(data: ITimeRecordResponse): Observable<any> {
        return this.apiService.post('/time_records/accept', data);
    }

    public fetchSubcontractorsExpenses(subcontractorId: string, timesheetId: string): Observable<any> {
        return this.apiService.get(`/expenses/getSubcontractorExpenses/one/${subcontractorId}?timesheet_id=${timesheetId}`);
    }

    public acceptExpense(data: any): Observable<any> {
        return this.apiService.post('/expenses/accept', data);
    }

    public rejectExpense(data: any): Observable<any> {
        return this.apiService.post('/expenses/reject', data);
    }

    public fetchWorkLocationTimeTrackingsAndExpenses(
        subcontractorID: string,
        workLocationID: string,
        data: any,
    ): Observable<{ trackings: ITimeRecord[]; expenses: IExpense[]; travelCosts: IExpense[] }> {
        return this.apiService.get(`/time_records/getSubcontractorDailySummary/mobile/${subcontractorID}/${workLocationID}`, data).pipe(
            map((data: { trackings: ITimeRecord[]; expenses: IExpense[] }) => {
                return {
                    trackings: data.trackings || [],
                    travelCosts: (<any>data.expenses).find((el) => el._id === 'TRAVEL_COST')?.expenses || [],
                    expenses: (<any>data.expenses).find((el) => el._id === 'EXPENSE')?.expenses || [],
                };
            }),
        );
    }

    public fetchPayrollEntriesFromPayrollForSubcontractor(payrollID: string): Observable<IPayrollEntry[]> {
        return this.apiService.get(`/subcontractors/payrolls/${payrollID}/payroll-entries`);
    }

    public exportTimesheetsToCSV(data: any): Observable<any> {
        return this.apiService.downloadGet(`/payrolls`, data);
    }

    public getTimesheetTemplateXLSX(id: string, options: { includeAdditionalFields: boolean }): Observable<any> {
        return this.apiService.downloadGet(`/payrolls/${id}/template`, options);
    }

    public getPaymentsSummary(id: string): Observable<IPaymentsSummary> {
        return this.apiService.get(`/payrolls/${id}/payments/summary`);
    }

    public runPaymentStep(id: string, data: any): Observable<any> {
        return this.apiService.post(`/payrolls/${id}/payments/run`, data);
    }

    public runAllInternalPaymentSteps(id: string): Observable<any> {
        return this.apiService.post(`/payrolls/${id}/payments/run/internal`);
    }

    public getPaymentsSubcontractors(id: string, data: any): Observable<IPagination<IPaymentSubcontractor>> {
        return this.apiService.get(`/payrolls/${id}/payments/subcontractors`, data);
    }
    public paySingleSubcontractor(payrollId: string, subcontractorId: string): Observable<any> {
        return this.apiService.post(`/payrolls/${payrollId}/payments/subcontractors/${subcontractorId}`);
    }
    public markSingleSubcontractorAsPaid(payrollId: string, subcontractorId: string): Observable<any> {
        return this.apiService.put(`/payrolls/${payrollId}/payments/subcontractors/${subcontractorId}/paid`);
    }

    public paymentsAuthenticateStart(): Observable<any> {
        return this.apiService.post(`/payments/authenticate/start`);
    }

    public paymentsAuthenticateConfirm(code: string): Observable<IPaymentAuth> {
        return this.apiService.post(`/payments/authenticate/confirm`, { code });
    }

    public fetchTimesheetsWeeklySummary(data: any): Observable<ITimesheetMoreInfoElement[]> {
        return this.apiService.get('/payrolls/weekly-summary', data).pipe(map((response: { results: ITimesheetMoreInfoElement[] }) => response.results));
    }

    public updatePayrollByUpload(id: string, file: File): Observable<any> {
        const formData = new FormData();
        formData.append('payroll', file || '');
        return this.apiService.put(`/payrolls/${id}/upload`, formData);
    }

    public downloadPayrollsWeeklyStatsCSV(): Observable<any> {
        return this.apiService.downloadGet('/payrolls/weekly-summary/csv');
    }
}
